import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, output, signal, viewChild } from '@angular/core';

/**
 * Hidden "DOOM.EXE" easter egg: a small Wolfenstein/Doom-style raycaster
 * (DDA walls with procedural textures, floor/ceiling casting, depth-buffered
 * billboard sprites, BFS-pathfinding enemies with fireballs, pickups).
 * Only reachable in terminal mode by typing "doom" (see App.handleGlobalKeydown).
 */

const W = 320;
const H = 200;
const HALF_H = H / 2;
const TEX = 32;
const SHADES = 16;
const PLANE_LEN = 0.66;

const MOVE_SPEED = 3;
const RUN_MULT = 1.7;
const TURN_SPEED = 2.6;
const MOUSE_SENS = 0.0025;
const PLAYER_RADIUS = 0.22;
const FIRE_COOLDOWN = 0.3;
const START_HP = 100;
const START_AMMO = 30;

const ENEMY_HP = 3;
const ENEMY_SPEED = 1.5;
const ENEMY_RADIUS = 0.3;
const ENEMY_SIGHT = 12;
const ENEMY_FIRE_RANGE = 10;
const MELEE_RANGE = 0.9;
const MELEE_DAMAGE = 10;
const FIREBALL_SPEED = 5;
const FIREBALL_LIFE = 4;

// 1-3 = wall textures, P = player, E = enemy, H = health, A = ammo.
const MAP_TEMPLATE = [
  '1111111111111111',
  '1E00000220000E01',
  '1011100000011101',
  '1010000H00000101',
  '1010022003300101',
  '10000200E0300001',
  '1000000000000A01',
  '1330000110000331',
  '10E0000110000E01',
  '1000000000000001',
  '1002200A00022001',
  '1002000330002001',
  '10H0000330000E01',
  '1011100000011101',
  '1P0000000000A001',
  '1111111111111111'
];
const MAP_W = MAP_TEMPLATE[0].length;
const MAP_H = MAP_TEMPLATE.length;

const PALETTE: Record<string, [number, number, number]> = {
  R: [150, 20, 20],
  r: [220, 60, 40],
  Y: [255, 200, 40],
  K: [30, 5, 5],
  W: [240, 240, 230],
  F: [255, 140, 0],
  G: [40, 220, 90],
  A: [170, 110, 10]
};

const IMP_HEAD = [
  '.....RRRRRR.....',
  '....RrrrrrrR....',
  '...RrYYrrYYrR...',
  '...RrYKrrKYrR...',
  '...RrrrrrrrrR...',
  '....RrKWWKrR....',
  '....RRKKKKRR....'
];
const IMP_HEAD_ATTACK = [
  '.....RRRRRR.....',
  '....RrrrrrrR....',
  '...RrWWrrWWrR...',
  '...RrWYrrYWrR...',
  '...RrrrrrrrrR...',
  '....RKFFFFKR....',
  '....RRKFFKRR....'
];
const IMP_BODY = [
  '..RRrRRRRRRrRR..',
  '.RrrRrrrrrrRrrR.',
  '.Rr.RrrrrrrR.rR.',
  '.Rr.RrrrrrrR.rR.',
  '.KK.RrrRRrrR.KK.'
];
const IMP_BODY_ATTACK = [
  'FR..RRRRRRRR..RF',
  'RrR.RrrrrrrR.RrR',
  '.RrRRrrrrrrRRrR.',
  '..RRRrrrrrrRRR..',
  '....RrrRRrrR....'
];
const IMP_LEGS_A = [
  '....RrR..RrR....',
  '....RrR..RrR....',
  '...RRrR..RrRR...',
  '...KKK....KKK...'
];
const IMP_LEGS_B = [
  '...RrR....RrR...',
  '...RrR....RrR...',
  '..RRrR....RrRR..',
  '..KKK......KKK..'
];
const EMPTY_ROW = '................';
const CORPSE = [
  ...Array<string>(11).fill(EMPTY_ROW),
  '.....RR....R....',
  '...RrrrRRrRrR...',
  '..RrrKrrrrrrrRR.',
  '.RrrrrrrKrrrrrR.',
  'KKKKKKKKKKKKKKKK'
];
const MEDKIT = [
  ...Array<string>(8).fill(EMPTY_ROW),
  '...KKKKKKKKKK...',
  '...KWWWWWWWWK...',
  '...KWWWGGWWWK...',
  '...KWWGGGGWWK...',
  '...KWWWGGWWWK...',
  '...KWWWWWWWWK...',
  '...KKKKKKKKKK...',
  EMPTY_ROW
];
const AMMO_BOX = [
  ...Array<string>(9).fill(EMPTY_ROW),
  '..KKKKKKKKKKKK..',
  '..KAAAAAAAAAAK..',
  '..KAYYAYYAYYAK..',
  '..KAYYAYYAYYAK..',
  '..KAAAAAAAAAAK..',
  '..KKKKKKKKKKKK..',
  EMPTY_ROW
];

interface SpriteTex {
  w: number;
  h: number;
  shades: Uint32Array[];
}

interface Enemy {
  x: number;
  y: number;
  hp: number;
  awake: boolean;
  cooldown: number;
  attackAnim: number;
  hurt: number;
  walkPhase: number;
}

interface Pickup {
  x: number;
  y: number;
  kind: 'health' | 'ammo';
  taken: boolean;
}

interface Fireball {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
}

interface RenderSprite {
  x: number;
  y: number;
  tex: SpriteTex;
  scale: number;
  floating: boolean;
  fullbright: boolean;
}

type GameStatus = 'playing' | 'won' | 'lost';

function pack(r: number, g: number, b: number): number {
  return ((255 << 24) | (b << 16) | (g << 8) | r) >>> 0;
}

function shadeFactor(level: number): number {
  return Math.pow(0.84, level);
}

function seededRandom(seed: number): () => number {
  let state = seed;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 4294967296;
  };
}

function buildShades(rgb: Array<[number, number, number] | null>): Uint32Array[] {
  const shades: Uint32Array[] = [];
  for (let level = 0; level < SHADES; level++) {
    const f = shadeFactor(level);
    const out = new Uint32Array(rgb.length);
    rgb.forEach((c, i) => {
      out[i] = c ? pack(Math.round(c[0] * f), Math.round(c[1] * f), Math.round(c[2] * f)) : 0;
    });
    shades.push(out);
  }
  return shades;
}

function wallTexture(kind: number): Uint32Array[] {
  const rand = seededRandom(kind * 7919);
  const px: Array<[number, number, number]> = [];
  const cellShade = Array.from({ length: 16 }, () => rand());
  for (let y = 0; y < TEX; y++) {
    for (let x = 0; x < TEX; x++) {
      const n = rand();
      if (kind === 1) {
        // Green brick.
        const row = Math.floor(y / 8);
        const offset = (row % 2) * 8;
        const mortar = y % 8 === 0 || (x + offset) % 16 === 0;
        px.push(mortar ? [10, 40, 15] : [25 + n * 15, 120 + n * 45 + cellShade[(row * 2 + Math.floor((x + offset) / 16)) % 16] * 30, 45 + n * 20]);
      } else if (kind === 2) {
        // Amber tech panel with rivets.
        const border = x < 2 || x > 29 || y < 2 || y > 29;
        const rivet = (x === 4 || x === 27) && (y === 4 || y === 27);
        if (rivet) {
          px.push([255, 210, 90]);
        } else if (border) {
          px.push([60, 40, 0]);
        } else if (y === 16 || y === 15) {
          px.push([80, 55, 5]);
        } else {
          px.push([140 + n * 25, 90 + n * 15, 10]);
        }
      } else {
        // Mossy stone blocks.
        const cell = (Math.floor(y / 8) * 4 + Math.floor(x / 8)) % 16;
        const edge = x % 8 === 0 || y % 8 === 0;
        const v = edge ? 35 : 70 + cellShade[cell] * 50 + n * 15;
        px.push([v * 0.6, v, v * 0.7]);
      }
    }
  }
  return buildShades(px);
}

function spriteFromArt(art: string[], transform?: (c: [number, number, number]) => [number, number, number]): SpriteTex {
  const w = Math.max(...art.map((row) => row.length));
  const h = art.length;
  const px: Array<[number, number, number] | null> = [];
  for (const row of art) {
    for (let x = 0; x < w; x++) {
      const c = PALETTE[row[x] ?? '.'];
      px.push(c ? (transform ? transform(c) : c) : null);
    }
  }
  return { w, h, shades: buildShades(px) };
}

function fireballSprite(): SpriteTex {
  const size = 16;
  const px: Array<[number, number, number] | null> = [];
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const d = Math.hypot(x - 7.5, y - 7.5);
      px.push(d < 2.5 ? [255, 250, 200] : d < 4.5 ? [255, 190, 40] : d < 6.5 ? [230, 70, 10] : null);
    }
  }
  return { w: size, h: size, shades: buildShades(px) };
}

const whiten = (c: [number, number, number]): [number, number, number] => [
  c[0] + (255 - c[0]) * 0.65,
  c[1] + (255 - c[1]) * 0.65,
  c[2] + (255 - c[2]) * 0.65
];

@Component({
  selector: 'app-doom-game',
  host: {
    role: 'dialog',
    'aria-modal': 'true',
    'aria-label': 'Doom mini-game',
    '(document:keydown)': 'onKeydown($event)',
    '(document:keyup)': 'onKeyup($event)',
    '(document:mousemove)': 'onMouseMove($event)',
    '(document:mouseup)': 'mouseFiring = false',
    '(document:pointerlockchange)': 'onPointerLockChange()',
    '(window:blur)': 'onBlur()'
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="doom-panel">
      <div class="doom-header">
        <span class="doom-title">*** DOOM.EXE ***</span>
        <span class="doom-stat" [class.doom-low]="hp() <= 25">HP: {{ hp() }}</span>
        <span class="doom-stat" [class.doom-low]="ammo() === 0">AMMO: {{ ammo() }}</span>
        <span class="doom-stat">KILLS: {{ kills() }}/{{ totalEnemies() }}</span>
        <button class="doom-close" type="button" (click)="requestClose()">[ESC] CLOSE</button>
      </div>
      <canvas
        #canvasEl
        [width]="canvasWidth"
        [height]="canvasHeight"
        class="doom-canvas"
        aria-label="Doom game viewport"
        (mousedown)="onCanvasMouseDown($event)"
      ></canvas>
      @if (status() !== 'playing') {
        <div class="doom-gameover">
          {{ status() === 'won' ? 'LEVEL CLEARED!' : 'YOU DIED' }} — KILLS {{ kills() }}/{{ totalEnemies() }} — PRESS ENTER TO RESTART
        </div>
      } @else {
        <div class="doom-hint">
          W/S MOVE · A/D STRAFE · ←/→ TURN · SPACE FIRE · SHIFT RUN · M MAP ·
          {{ mouseLocked() ? 'MOUSE LOOK ON (ESC TO RELEASE)' : 'CLICK VIEW FOR MOUSE LOOK' }} · ESC EXIT
        </div>
      }
    </div>
  `,
  styles: [`
    :host {
      position: fixed;
      inset: 0;
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 16px;
      background: rgba(0, 0, 0, 0.85);
      overflow: auto;
    }

    .doom-panel {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
      padding: 16px;
      background: #010c05;
      border: 2px solid #33ff66;
      border-radius: 4px;
      box-shadow: 0 0 24px rgba(51, 255, 102, 0.45);
      font-family: 'IBM Plex Mono', 'Courier New', monospace;
      color: #33ff66;
    }

    .doom-header {
      display: flex;
      align-items: center;
      gap: 18px;
      width: 100%;
      justify-content: space-between;
      flex-wrap: wrap;
    }

    .doom-title {
      font-weight: bold;
      letter-spacing: 2px;
      color: #ffb000;
      text-shadow: 0 0 6px rgba(255, 176, 0, 0.7);
    }

    .doom-stat {
      font-size: 0.9rem;
    }

    .doom-low {
      color: #ff6b6b;
    }

    .doom-close {
      background: transparent;
      border: 1px solid #33ff66;
      color: #33ff66;
      font-family: inherit;
      padding: 4px 10px;
      cursor: pointer;
      border-radius: 2px;
    }

    .doom-close:hover,
    .doom-close:focus-visible {
      background: rgba(51, 255, 102, 0.15);
    }

    .doom-canvas {
      width: auto;
      height: min(400px, calc(100vh - 200px));
      max-width: calc(100vw - 64px);
      image-rendering: pixelated;
      background: #000;
      border: 1px solid rgba(51, 255, 102, 0.5);
      cursor: crosshair;
    }

    .doom-gameover,
    .doom-hint {
      font-size: 0.8rem;
      text-align: center;
      max-width: 640px;
    }
  `]
})
export class DoomGame implements OnInit, OnDestroy {
  private readonly canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('canvasEl');
  private ctx: CanvasRenderingContext2D | null = null;
  private image: ImageData | null = null;
  private pixels: Uint32Array | null = null;
  private readonly zBuffer = new Float64Array(W);
  private frameId: number | undefined;
  private lastTime = 0;

  readonly canvasWidth = W;
  readonly canvasHeight = H;

  readonly close = output<void>();
  readonly hp = signal(START_HP);
  readonly ammo = signal(START_AMMO);
  readonly kills = signal(0);
  readonly totalEnemies = signal(0);
  readonly status = signal<GameStatus>('playing');
  readonly mouseLocked = signal(false);

  private readonly wallTextures = [wallTexture(1), wallTexture(1), wallTexture(2), wallTexture(3)];
  private readonly impWalkA = spriteFromArt([...IMP_HEAD, ...IMP_BODY, ...IMP_LEGS_A]);
  private readonly impWalkB = spriteFromArt([...IMP_HEAD, ...IMP_BODY, ...IMP_LEGS_B]);
  private readonly impAttack = spriteFromArt([...IMP_HEAD_ATTACK, ...IMP_BODY_ATTACK, ...IMP_LEGS_A]);
  private readonly impHurt = spriteFromArt([...IMP_HEAD, ...IMP_BODY, ...IMP_LEGS_A], whiten);
  private readonly impCorpse = spriteFromArt(CORPSE);
  private readonly medkitTex = spriteFromArt(MEDKIT);
  private readonly ammoTex = spriteFromArt(AMMO_BOX);
  private readonly fireballTex = fireballSprite();
  private readonly floorColors = this.buildSurfaceColors([[22, 75, 32], [15, 52, 23], [8, 30, 12]]);
  private readonly ceilingColors = this.buildSurfaceColors([[10, 28, 16], [7, 20, 11], [18, 45, 24]]);

  private readonly pressed = new Set<string>();
  mouseFiring = false;
  private fireQueued = false;
  private pendingMouseTurn = 0;

  private map: number[][] = [];
  private player = { x: 1.5, y: 1.5, angle: 0 };
  private enemies: Enemy[] = [];
  private pickups: Pickup[] = [];
  private fireballs: Fireball[] = [];
  private distField = new Int16Array(MAP_W * MAP_H);
  private distFieldCell = -1;

  private fireCooldown = 0;
  private muzzleTimer = 0;
  private damageFlash = 0;
  private pickupFlash = 0;
  private noAmmoTimer = 0;
  private walkPhase = 0;
  private showMap = true;

  ngOnInit(): void {
    this.resetState();
    const canvas = this.canvasRef().nativeElement;
    this.ctx = canvas.getContext('2d');
    if (this.ctx) {
      this.image = this.ctx.createImageData(W, H);
      this.pixels = new Uint32Array(this.image.data.buffer);
    }
    this.lastTime = performance.now();
    this.frameId = requestAnimationFrame((t) => this.loop(t));
  }

  ngOnDestroy(): void {
    if (this.frameId !== undefined) {
      cancelAnimationFrame(this.frameId);
    }
    this.releasePointer();
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      if (document.pointerLockElement === this.canvasRef().nativeElement) {
        this.releasePointer();
      } else {
        this.requestClose();
      }
      return;
    }
    if (event.key === 'Enter' && this.status() !== 'playing') {
      this.resetState();
      return;
    }
    if (event.code === 'KeyM') {
      this.showMap = !this.showMap;
      return;
    }
    const handled = [
      'KeyW', 'KeyA', 'KeyS', 'KeyD', 'KeyQ', 'KeyE',
      'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
      'Space', 'ShiftLeft', 'ShiftRight', 'ControlLeft', 'ControlRight'
    ];
    if (handled.includes(event.code)) {
      event.preventDefault();
      if (!event.repeat && ['Space', 'ControlLeft', 'ControlRight'].includes(event.code)) {
        this.fireQueued = true;
      }
      this.pressed.add(event.code);
    }
  }

  onKeyup(event: KeyboardEvent): void {
    this.pressed.delete(event.code);
  }

  onMouseMove(event: MouseEvent): void {
    if (this.mouseLocked()) {
      this.pendingMouseTurn += event.movementX * MOUSE_SENS;
    }
  }

  onCanvasMouseDown(event: MouseEvent): void {
    event.preventDefault();
    const canvas = this.canvasRef().nativeElement;
    if (document.pointerLockElement !== canvas) {
      canvas.requestPointerLock?.();
      return;
    }
    this.mouseFiring = true;
    this.fireQueued = true;
  }

  onPointerLockChange(): void {
    this.mouseLocked.set(document.pointerLockElement === this.canvasRef().nativeElement);
    if (!this.mouseLocked()) {
      this.mouseFiring = false;
    }
  }

  onBlur(): void {
    this.pressed.clear();
    this.mouseFiring = false;
  }

  requestClose(): void {
    this.close.emit();
  }

  private releasePointer(): void {
    if (document.pointerLockElement && document.pointerLockElement === this.canvasRef().nativeElement) {
      document.exitPointerLock?.();
    }
  }

  private buildSurfaceColors(variants: Array<[number, number, number]>): Uint32Array[] {
    return variants.map((c) => {
      const out = new Uint32Array(SHADES);
      for (let level = 0; level < SHADES; level++) {
        const f = shadeFactor(level);
        out[level] = pack(Math.round(c[0] * f), Math.round(c[1] * f), Math.round(c[2] * f));
      }
      return out;
    });
  }

  private resetState(): void {
    this.map = [];
    this.enemies = [];
    this.pickups = [];
    this.fireballs = [];
    MAP_TEMPLATE.forEach((row, y) => {
      const cells: number[] = [];
      [...row].forEach((ch, x) => {
        const cx = x + 0.5;
        const cy = y + 0.5;
        if (ch >= '1' && ch <= '9') {
          cells.push(Number(ch));
          return;
        }
        cells.push(0);
        if (ch === 'P') {
          this.player = { x: cx, y: cy, angle: -Math.PI / 2 };
        } else if (ch === 'E') {
          this.enemies.push({ x: cx, y: cy, hp: ENEMY_HP, awake: false, cooldown: 1 + Math.random(), attackAnim: 0, hurt: 0, walkPhase: Math.random() * 10 });
        } else if (ch === 'H') {
          this.pickups.push({ x: cx, y: cy, kind: 'health', taken: false });
        } else if (ch === 'A') {
          this.pickups.push({ x: cx, y: cy, kind: 'ammo', taken: false });
        }
      });
      this.map.push(cells);
    });
    this.distFieldCell = -1;
    this.hp.set(START_HP);
    this.ammo.set(START_AMMO);
    this.kills.set(0);
    this.totalEnemies.set(this.enemies.length);
    this.status.set('playing');
    this.pressed.clear();
    this.mouseFiring = false;
    this.fireQueued = false;
    this.fireCooldown = 0;
    this.muzzleTimer = 0;
    this.damageFlash = 0;
    this.pickupFlash = 0;
    this.noAmmoTimer = 0;
  }

  private loop(time: number): void {
    const dt = Math.min(0.05, (time - this.lastTime) / 1000);
    this.lastTime = time;
    if (this.status() === 'playing') {
      this.update(dt);
    }
    this.render();
    this.frameId = requestAnimationFrame((t) => this.loop(t));
  }

  // ---------------------------------------------------------------- update

  private update(dt: number): void {
    this.fireCooldown = Math.max(0, this.fireCooldown - dt);
    this.muzzleTimer = Math.max(0, this.muzzleTimer - dt);
    this.damageFlash = Math.max(0, this.damageFlash - dt * 2.5);
    this.pickupFlash = Math.max(0, this.pickupFlash - dt * 3);
    this.noAmmoTimer = Math.max(0, this.noAmmoTimer - dt);

    this.updatePlayer(dt);
    this.updateDistField();
    this.updateEnemies(dt);
    this.updateFireballs(dt);
    this.updatePickups();

    if (this.hp() <= 0) {
      this.status.set('lost');
      this.releasePointer();
    } else if (this.enemies.every((e) => e.hp <= 0)) {
      this.status.set('won');
      this.releasePointer();
    }
  }

  private isDown(...codes: string[]): boolean {
    return codes.some((c) => this.pressed.has(c));
  }

  private updatePlayer(dt: number): void {
    const turn = (this.isDown('ArrowRight', 'KeyE') ? 1 : 0) - (this.isDown('ArrowLeft', 'KeyQ') ? 1 : 0);
    this.player.angle += turn * TURN_SPEED * dt + this.pendingMouseTurn;
    this.pendingMouseTurn = 0;

    const forward = (this.isDown('KeyW', 'ArrowUp') ? 1 : 0) - (this.isDown('KeyS', 'ArrowDown') ? 1 : 0);
    const strafe = (this.isDown('KeyD') ? 1 : 0) - (this.isDown('KeyA') ? 1 : 0);
    if (forward !== 0 || strafe !== 0) {
      const cos = Math.cos(this.player.angle);
      const sin = Math.sin(this.player.angle);
      let mx = cos * forward - sin * strafe;
      let my = sin * forward + cos * strafe;
      const len = Math.hypot(mx, my);
      mx /= len;
      my /= len;
      const speed = MOVE_SPEED * (this.isDown('ShiftLeft', 'ShiftRight') ? RUN_MULT : 1) * dt;
      this.moveBody(this.player, mx * speed, my * speed, PLAYER_RADIUS);
      this.walkPhase += dt * 10;
    }

    // Queued shots make quick taps register even if the key is released before the next frame.
    if ((this.fireQueued || this.isDown('Space', 'ControlLeft', 'ControlRight') || this.mouseFiring) && this.shoot()) {
      this.fireQueued = false;
    }
  }

  /** Axis-separated movement so bodies slide along walls instead of sticking. */
  private moveBody(body: { x: number; y: number }, dx: number, dy: number, radius: number): void {
    if (!this.collides(body.x + dx, body.y, radius)) {
      body.x += dx;
    }
    if (!this.collides(body.x, body.y + dy, radius)) {
      body.y += dy;
    }
  }

  private collides(x: number, y: number, r: number): boolean {
    return this.isWall(x - r, y - r) || this.isWall(x + r, y - r) || this.isWall(x - r, y + r) || this.isWall(x + r, y + r);
  }

  private isWall(x: number, y: number): boolean {
    const cx = Math.floor(x);
    const cy = Math.floor(y);
    if (cx < 0 || cy < 0 || cx >= MAP_W || cy >= MAP_H) {
      return true;
    }
    return this.map[cy][cx] > 0;
  }

  private hasLineOfSight(x0: number, y0: number, x1: number, y1: number): boolean {
    const dist = Math.hypot(x1 - x0, y1 - y0);
    const steps = Math.ceil(dist / 0.1);
    for (let i = 1; i < steps; i++) {
      const t = i / steps;
      if (this.isWall(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t)) {
        return false;
      }
    }
    return true;
  }

  /** BFS flow field from the player's cell, used by enemies to path around walls. */
  private updateDistField(): void {
    const px = Math.floor(this.player.x);
    const py = Math.floor(this.player.y);
    const cell = py * MAP_W + px;
    if (cell === this.distFieldCell) {
      return;
    }
    this.distFieldCell = cell;
    this.distField.fill(-1);
    this.distField[cell] = 0;
    const queue = [cell];
    for (let head = 0; head < queue.length; head++) {
      const current = queue[head];
      const cx = current % MAP_W;
      const cy = Math.floor(current / MAP_W);
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const nx = cx + dx;
        const ny = cy + dy;
        const next = ny * MAP_W + nx;
        if (this.map[ny]?.[nx] === 0 && this.distField[next] === -1) {
          this.distField[next] = this.distField[current] + 1;
          queue.push(next);
        }
      }
    }
  }

  private fieldAt(x: number, y: number): number {
    const v = this.distField[Math.floor(y) * MAP_W + Math.floor(x)];
    return v < 0 ? 9999 : v;
  }

  private updateEnemies(dt: number): void {
    const alive = this.enemies.filter((e) => e.hp > 0);
    for (const e of alive) {
      e.cooldown = Math.max(0, e.cooldown - dt);
      e.attackAnim = Math.max(0, e.attackAnim - dt);
      e.hurt = Math.max(0, e.hurt - dt);

      const dx = this.player.x - e.x;
      const dy = this.player.y - e.y;
      const dist = Math.hypot(dx, dy);
      const los = dist < ENEMY_SIGHT && this.hasLineOfSight(e.x, e.y, this.player.x, this.player.y);

      if (!e.awake && (los || this.fieldAt(e.x, e.y) <= 3)) {
        e.awake = true;
        e.cooldown = Math.max(e.cooldown, 0.6);
      }
      if (!e.awake) {
        continue;
      }

      if (dist < MELEE_RANGE) {
        if (e.cooldown <= 0) {
          this.damagePlayer(MELEE_DAMAGE);
          e.cooldown = 1;
          e.attackAnim = 0.3;
        }
      } else if (los && dist < ENEMY_FIRE_RANGE && e.cooldown <= 0 && Math.random() < dt * 1.5) {
        const spread = (Math.random() - 0.5) * 0.12;
        const angle = Math.atan2(dy, dx) + spread;
        this.fireballs.push({
          x: e.x + Math.cos(angle) * 0.4,
          y: e.y + Math.sin(angle) * 0.4,
          vx: Math.cos(angle) * FIREBALL_SPEED,
          vy: Math.sin(angle) * FIREBALL_SPEED,
          life: FIREBALL_LIFE
        });
        e.cooldown = 1.6 + Math.random() * 1.4;
        e.attackAnim = 0.4;
      }

      if (e.attackAnim > 0 || dist < MELEE_RANGE * 0.9) {
        continue;
      }

      let tx: number;
      let ty: number;
      if (los && dist < 3) {
        tx = this.player.x;
        ty = this.player.y;
      } else {
        const cx = Math.floor(e.x);
        const cy = Math.floor(e.y);
        let best = this.fieldAt(e.x, e.y);
        tx = cx + 0.5;
        ty = cy + 0.5;
        for (const [ox, oy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
          const v = this.fieldAt(cx + ox + 0.5, cy + oy + 0.5);
          if (this.map[cy + oy]?.[cx + ox] === 0 && v < best) {
            best = v;
            tx = cx + ox + 0.5;
            ty = cy + oy + 0.5;
          }
        }
      }
      const mdx = tx - e.x;
      const mdy = ty - e.y;
      const mlen = Math.hypot(mdx, mdy);
      if (mlen > 0.01) {
        const step = Math.min(mlen, ENEMY_SPEED * dt);
        this.moveBody(e, (mdx / mlen) * step, (mdy / mlen) * step, ENEMY_RADIUS * 0.8);
        e.walkPhase += dt * 6;
      }
    }

    // Keep enemies from stacking on top of each other.
    for (let i = 0; i < alive.length; i++) {
      for (let j = i + 1; j < alive.length; j++) {
        const a = alive[i];
        const b = alive[j];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const d = Math.hypot(dx, dy);
        const min = ENEMY_RADIUS * 2;
        if (d > 0.0001 && d < min) {
          const push = (min - d) / 2;
          this.moveBody(a, (-dx / d) * push, (-dy / d) * push, ENEMY_RADIUS * 0.8);
          this.moveBody(b, (dx / d) * push, (dy / d) * push, ENEMY_RADIUS * 0.8);
        }
      }
    }
  }

  private updateFireballs(dt: number): void {
    this.fireballs = this.fireballs.filter((f) => {
      f.x += f.vx * dt;
      f.y += f.vy * dt;
      f.life -= dt;
      if (f.life <= 0 || this.isWall(f.x, f.y)) {
        return false;
      }
      if (Math.hypot(f.x - this.player.x, f.y - this.player.y) < 0.4) {
        this.damagePlayer(7 + Math.floor(Math.random() * 7));
        return false;
      }
      return true;
    });
  }

  private updatePickups(): void {
    for (const p of this.pickups) {
      if (p.taken || Math.hypot(p.x - this.player.x, p.y - this.player.y) > 0.6) {
        continue;
      }
      if (p.kind === 'health' && this.hp() < START_HP) {
        this.hp.update((v) => Math.min(START_HP, v + 25));
        p.taken = true;
        this.pickupFlash = 1;
      } else if (p.kind === 'ammo') {
        this.ammo.update((v) => v + 12);
        p.taken = true;
        this.pickupFlash = 1;
      }
    }
  }

  private damagePlayer(amount: number): void {
    this.hp.update((v) => Math.max(0, v - amount));
    this.damageFlash = 1;
  }

  /** Returns true when a shot attempt was consumed (fired or dry-fired). */
  private shoot(): boolean {
    if (this.fireCooldown > 0) {
      return false;
    }
    if (this.ammo() <= 0) {
      this.noAmmoTimer = 1;
      this.fireCooldown = FIRE_COOLDOWN;
      return true;
    }
    this.ammo.update((v) => v - 1);
    this.fireCooldown = FIRE_COOLDOWN;
    this.muzzleTimer = 0.08;

    const cos = Math.cos(this.player.angle);
    const sin = Math.sin(this.player.angle);
    const wallDist = this.zBuffer[W >> 1] || Infinity;
    let target: Enemy | null = null;
    let targetDepth = Infinity;
    for (const e of this.enemies) {
      if (e.hp <= 0) {
        continue;
      }
      const sx = e.x - this.player.x;
      const sy = e.y - this.player.y;
      const depth = sx * cos + sy * sin;
      const lateral = -sx * sin + sy * cos;
      if (depth > 0.1 && depth < wallDist && Math.abs(lateral) < ENEMY_RADIUS * 1.1 && depth < targetDepth) {
        target = e;
        targetDepth = depth;
      }
    }

    // Gunfire is loud: wake up anything nearby.
    for (const e of this.enemies) {
      if (this.fieldAt(e.x, e.y) <= 8) {
        e.awake = true;
      }
    }

    if (target) {
      target.hp -= 1;
      target.hurt = 0.15;
      target.awake = true;
      if (target.hp <= 0) {
        this.kills.update((v) => v + 1);
      }
    }
    return true;
  }

  // ---------------------------------------------------------------- render

  private render(): void {
    const ctx = this.ctx;
    const pixels = this.pixels;
    if (!ctx || !pixels || !this.image) {
      return;
    }
    const dirX = Math.cos(this.player.angle);
    const dirY = Math.sin(this.player.angle);
    const planeX = -dirY * PLANE_LEN;
    const planeY = dirX * PLANE_LEN;

    this.renderFloorCeiling(pixels, dirX, dirY, planeX, planeY);
    this.renderWalls(pixels, dirX, dirY, planeX, planeY);
    this.renderSprites(pixels, dirX, dirY);
    ctx.putImageData(this.image, 0, 0);
    this.renderOverlay(ctx);
  }

  private renderFloorCeiling(pixels: Uint32Array, dirX: number, dirY: number, planeX: number, planeY: number): void {
    const rd0x = dirX - planeX;
    const rd0y = dirY - planeY;
    const rd1x = dirX + planeX;
    const rd1y = dirY + planeY;
    for (let y = HALF_H; y < H; y++) {
      const p = y - HALF_H + 0.5;
      const rowDist = HALF_H / p;
      const stepX = (rowDist * (rd1x - rd0x)) / W;
      const stepY = (rowDist * (rd1y - rd0y)) / W;
      let fx = this.player.x + rowDist * rd0x;
      let fy = this.player.y + rowDist * rd0y;
      const shade = Math.min(SHADES - 1, Math.floor(rowDist * 1.4));
      const floorRow = y * W;
      const ceilRow = (H - 1 - y) * W;
      for (let x = 0; x < W; x++) {
        const cx = Math.floor(fx);
        const cy = Math.floor(fy);
        const lx = fx - cx;
        const ly = fy - cy;
        const line = lx < 0.04 || ly < 0.04;
        const variant = line ? 2 : (cx + cy) & 1;
        pixels[floorRow + x] = this.floorColors[variant][shade];
        pixels[ceilRow + x] = this.ceilingColors[line ? 2 : 0][shade];
        fx += stepX;
        fy += stepY;
      }
    }
  }

  private renderWalls(pixels: Uint32Array, dirX: number, dirY: number, planeX: number, planeY: number): void {
    const px = this.player.x;
    const py = this.player.y;
    for (let x = 0; x < W; x++) {
      const cameraX = (2 * x) / W - 1;
      const rayX = dirX + planeX * cameraX;
      const rayY = dirY + planeY * cameraX;
      let mapX = Math.floor(px);
      let mapY = Math.floor(py);
      const deltaX = rayX === 0 ? 1e30 : Math.abs(1 / rayX);
      const deltaY = rayY === 0 ? 1e30 : Math.abs(1 / rayY);
      const stepX = rayX < 0 ? -1 : 1;
      const stepY = rayY < 0 ? -1 : 1;
      let sideX = rayX < 0 ? (px - mapX) * deltaX : (mapX + 1 - px) * deltaX;
      let sideY = rayY < 0 ? (py - mapY) * deltaY : (mapY + 1 - py) * deltaY;
      let side = 0;
      let wallType = 0;
      for (let i = 0; i < 64 && wallType === 0; i++) {
        if (sideX < sideY) {
          sideX += deltaX;
          mapX += stepX;
          side = 0;
        } else {
          sideY += deltaY;
          mapY += stepY;
          side = 1;
        }
        wallType = this.map[mapY]?.[mapX] ?? 1;
      }
      const perpDist = Math.max(0.0001, side === 0 ? sideX - deltaX : sideY - deltaY);
      this.zBuffer[x] = perpDist;

      const lineH = H / perpDist;
      const drawStart = Math.max(0, Math.floor(HALF_H - lineH / 2));
      const drawEnd = Math.min(H - 1, Math.floor(HALF_H + lineH / 2));

      let wallX = side === 0 ? py + perpDist * rayY : px + perpDist * rayX;
      wallX -= Math.floor(wallX);
      let texX = Math.floor(wallX * TEX);
      if ((side === 0 && rayX > 0) || (side === 1 && rayY < 0)) {
        texX = TEX - texX - 1;
      }

      const shade = Math.min(SHADES - 1, Math.floor(perpDist * 1.3) + (side === 1 ? 2 : 0));
      const tex = this.wallTextures[wallType][shade];
      const step = TEX / lineH;
      let texPos = (drawStart - HALF_H + lineH / 2) * step;
      for (let y = drawStart; y <= drawEnd; y++) {
        pixels[y * W + x] = tex[((texPos | 0) & (TEX - 1)) * TEX + texX];
        texPos += step;
      }
    }
  }

  private renderSprites(pixels: Uint32Array, dirX: number, dirY: number): void {
    const sprites: RenderSprite[] = [];
    for (const e of this.enemies) {
      let tex: SpriteTex;
      if (e.hp <= 0) {
        tex = this.impCorpse;
      } else if (e.hurt > 0) {
        tex = this.impHurt;
      } else if (e.attackAnim > 0) {
        tex = this.impAttack;
      } else {
        tex = Math.floor(e.walkPhase) % 2 === 0 ? this.impWalkA : this.impWalkB;
      }
      sprites.push({ x: e.x, y: e.y, tex, scale: 0.85, floating: false, fullbright: e.attackAnim > 0 });
    }
    for (const p of this.pickups) {
      if (!p.taken) {
        sprites.push({ x: p.x, y: p.y, tex: p.kind === 'health' ? this.medkitTex : this.ammoTex, scale: 0.6, floating: false, fullbright: false });
      }
    }
    for (const f of this.fireballs) {
      sprites.push({ x: f.x, y: f.y, tex: this.fireballTex, scale: 0.3, floating: true, fullbright: true });
    }

    const rightX = -dirY;
    const rightY = dirX;
    const projected = sprites
      .map((s) => {
        const sx = s.x - this.player.x;
        const sy = s.y - this.player.y;
        return { s, depth: sx * dirX + sy * dirY, lateral: (sx * rightX + sy * rightY) / PLANE_LEN };
      })
      .filter((p) => p.depth > 0.15)
      .sort((a, b) => b.depth - a.depth);

    for (const { s, depth, lateral } of projected) {
      const screenX = (W / 2) * (1 + lateral / depth);
      const lineH = H / depth;
      const size = s.scale * lineH;
      const bottom = s.floating ? HALF_H + size / 2 : HALF_H + lineH / 2;
      const top = bottom - size;
      const left = screenX - size / 2;
      const shade = s.fullbright ? 0 : Math.min(SHADES - 1, Math.floor(depth * 1.3));
      const texels = s.tex.shades[shade];
      const x0 = Math.max(0, Math.floor(left));
      const x1 = Math.min(W - 1, Math.floor(left + size));
      const y0 = Math.max(0, Math.floor(top));
      const y1 = Math.min(H - 1, Math.floor(bottom));
      for (let x = x0; x <= x1; x++) {
        if (depth >= this.zBuffer[x]) {
          continue;
        }
        const tx = Math.min(s.tex.w - 1, Math.floor(((x - left) * s.tex.w) / size));
        for (let y = y0; y <= y1; y++) {
          const ty = Math.min(s.tex.h - 1, Math.floor(((y - top) * s.tex.h) / size));
          const c = texels[ty * s.tex.w + tx];
          if (c !== 0) {
            pixels[y * W + x] = c;
          }
        }
      }
    }
  }

  private renderOverlay(ctx: CanvasRenderingContext2D): void {
    // Weapon with walk bob and recoil.
    const bobX = Math.sin(this.walkPhase) * 5;
    const bobY = Math.abs(Math.cos(this.walkPhase)) * 4 + (this.muzzleTimer > 0 ? -6 : 0);
    const gx = W / 2 + bobX;
    const gy = H + bobY;
    if (this.muzzleTimer > 0) {
      ctx.fillStyle = 'rgba(255, 176, 0, 0.9)';
      ctx.beginPath();
      ctx.arc(gx, gy - 66, 11, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#fff6d0';
      ctx.beginPath();
      ctx.arc(gx, gy - 66, 5, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = '#10261a';
    ctx.fillRect(gx - 7, gy - 60, 14, 22);
    ctx.fillStyle = '#1d4a2c';
    ctx.fillRect(gx - 12, gy - 40, 24, 40);
    ctx.fillStyle = '#33ff66';
    ctx.fillRect(gx - 7, gy - 60, 2, 20);
    ctx.fillRect(gx - 12, gy - 40, 24, 2);
    ctx.fillStyle = '#6b4a1a';
    ctx.fillRect(gx - 20, gy - 18, 40, 18);

    // Crosshair.
    ctx.fillStyle = '#33ff66';
    ctx.fillRect(W / 2 - 4, HALF_H, 3, 1);
    ctx.fillRect(W / 2 + 2, HALF_H, 3, 1);
    ctx.fillRect(W / 2, HALF_H - 4, 1, 3);
    ctx.fillRect(W / 2, HALF_H + 2, 1, 3);

    if (this.damageFlash > 0) {
      ctx.fillStyle = `rgba(255, 0, 0, ${this.damageFlash * 0.45})`;
      ctx.fillRect(0, 0, W, H);
    }
    if (this.pickupFlash > 0) {
      ctx.fillStyle = `rgba(255, 176, 0, ${this.pickupFlash * 0.25})`;
      ctx.fillRect(0, 0, W, H);
    }
    if (this.noAmmoTimer > 0) {
      ctx.fillStyle = '#ff6b6b';
      ctx.font = '10px "IBM Plex Mono", monospace';
      ctx.textAlign = 'center';
      ctx.fillText('NO AMMO', W / 2, HALF_H + 22);
    }

    if (this.showMap) {
      this.renderMinimap(ctx);
    }

    if (this.status() !== 'playing') {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = this.status() === 'won' ? '#33ff66' : '#ff4d4d';
      ctx.font = 'bold 20px "IBM Plex Mono", monospace';
      ctx.textAlign = 'center';
      ctx.fillText(this.status() === 'won' ? 'LEVEL CLEARED' : 'YOU DIED', W / 2, HALF_H);
      ctx.font = '10px "IBM Plex Mono", monospace';
      ctx.fillStyle = '#ffb000';
      ctx.fillText('PRESS ENTER TO RESTART', W / 2, HALF_H + 18);
    }
  }

  private renderMinimap(ctx: CanvasRenderingContext2D): void {
    const cell = 3;
    const ox = 4;
    const oy = 4;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.fillRect(ox - 1, oy - 1, MAP_W * cell + 2, MAP_H * cell + 2);
    ctx.fillStyle = 'rgba(51, 255, 102, 0.7)';
    for (let y = 0; y < MAP_H; y++) {
      for (let x = 0; x < MAP_W; x++) {
        if (this.map[y][x] > 0) {
          ctx.fillRect(ox + x * cell, oy + y * cell, cell, cell);
        }
      }
    }
    ctx.fillStyle = '#ff4d4d';
    for (const e of this.enemies) {
      if (e.hp > 0) {
        ctx.fillRect(ox + e.x * cell - 1, oy + e.y * cell - 1, 2, 2);
      }
    }
    ctx.fillStyle = '#ffb000';
    ctx.fillRect(ox + this.player.x * cell - 1, oy + this.player.y * cell - 1, 3, 3);
    ctx.strokeStyle = '#ffb000';
    ctx.beginPath();
    ctx.moveTo(ox + this.player.x * cell, oy + this.player.y * cell);
    ctx.lineTo(
      ox + (this.player.x + Math.cos(this.player.angle) * 1.5) * cell,
      oy + (this.player.y + Math.sin(this.player.angle) * 1.5) * cell
    );
    ctx.stroke();
  }
}
